import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success("Welcome back!");
      nav("/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-md mx-auto px-4 py-12">
        <div className="brand-card p-8 sm:p-10">
          <div className="overline mb-3">Welcome back</div>
          <h1 className="text-3xl font-extrabold text-[#3D405B] tracking-tight">Sign in to continue</h1>
          <p className="text-sm text-[#7A7D96] mt-2">Pick up where you left off — your sessions and chats are waiting.</p>

          <button onClick={onGoogle} className="w-full mt-6 flex items-center justify-center gap-3 px-5 py-3 rounded-2xl border-2 border-[#3D405B]/15 hover:bg-[#3D405B]/5 transition-all font-semibold text-[#3D405B]" data-testid="login-google-btn">
            <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden><path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.1 29 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 20-7.6 20-21 0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14l6.6 4.8C14.7 14.4 19 11 24 11c3 0 5.8 1.1 7.9 3l5.7-5.7C33.6 5.1 29 3 24 3 16.3 3 9.6 7.4 6.3 14z"/><path fill="#4CAF50" d="M24 45c5 0 9.5-1.9 13-5l-6-5.1c-2 1.4-4.4 2.1-7 2.1-5.3 0-9.7-3.4-11.3-8L6 33.5C9.2 40.4 16 45 24 45z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4-3.9 5.4l6 5.1c-.4.4 6.6-4.9 6.6-14.5 0-1.2-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#3D405B]/10" />
            <span className="text-xs uppercase tracking-[0.2em] text-[#7A7D96] font-bold">or</span>
            <div className="flex-1 h-px bg-[#3D405B]/10" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Email<span className="text-primary ml-0.5" aria-label="required">*</span></label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3D405B] uppercase tracking-wider mb-2">Password<span className="text-primary ml-0.5" aria-label="required">*</span></label>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none transition-all"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-xl" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={submitting} className="w-full px-5 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60 disabled:translate-y-0" data-testid="login-submit-btn">
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-right">
              <Link to="/forgot-password" className="text-sm font-semibold text-primary hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
          </form>

          <p className="text-sm text-center text-[#7A7D96] mt-6">
            New to Hellofoundation Youth Mental Wellness & Support? <Link to="/register" className="text-primary font-bold hover:underline" data-testid="login-register-link">Create an account</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
